"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { assetService, type AssetListItem, type AssetType, type AssetWithPermission, type AssetSharingStatus } from '@/services/asset-service';
import { Copy, MoreHorizontal, Trash2, Edit, Upload, Users, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// Import shadcn/ui components
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import AssetUploadPanel from './asset-upload-panel.tsx';
import { AssetCreationDialog } from './asset-creation-dialog';
import { AssetFilterControls } from './asset-filter-controls.tsx';
import { SortButton } from './sort-button';
import { AssetShareModal } from './asset-share-modal';

import { SelectAllCheckbox } from './select-all-checkbox';
import { SkeletonRow } from './skeleton-row';
import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { AssetCard } from './responsive-asset-card';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { getRoleLabel, getRoleBadgeColor, canUserPerformAction } from '@/lib/asset-permission-utils';
import { AssetSharingIndicators } from './asset-sharing-indicators';
import { useUser } from '@/context/user-provider';

type Props = {
    title?: string;
    onRowClick?: (asset: AssetWithPermission) => void;
    onAdd?: () => void;
};

export default function AssetsList({
    title = 'Assets',
    onRowClick,
    onAdd,
}: Props) {
    // --- STATE MANAGEMENT ---
    const [items, setItems] = useState<AssetWithPermission[]>([]);
    const [loading, setLoading] = useState(true);
    const [availableTypes, setAvailableTypes] = useState<AssetType[]>([]);

    // Simplified filter state, managed by the child component
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<string[] | undefined>(undefined);
    const [includePublic, setIncludePublic] = useState(false);

    const [sorting, setSorting] = useState({ sortBy: 'updatedAt', sortDir: 'desc' });
    const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });
    const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
    const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
    const [isCreationDialogOpen, setIsCreationDialogOpen] = useState(false);

    // Sharing modal state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareModalAsset, setShareModalAsset] = useState<AssetWithPermission | null>(null);

    // Sharing indicators state (pre-loaded after 500ms delay)
    const [assetSharingInfo, setAssetSharingInfo] = useState<Map<string, AssetSharingStatus>>(new Map());
    const [loadingSharingFor, setLoadingSharingFor] = useState<string | null>(null);

    const navigate = useNavigate();
    const { isMobile, isTablet } = useBreakpoint();
    const { user, activeOrganization } = useUser();

    // Client-side search for the current page results - search across multiple fields
    const processedItems = useMemo(() => {
        let sortedItems = [...items].sort((a, b) => {
            const dir = sorting.sortDir === 'asc' ? 1 : -1;
            const aVal = a[sorting.sortBy as keyof AssetWithPermission] ?? '';
            const bVal = b[sorting.sortBy as keyof AssetWithPermission] ?? '';
            if (aVal < bVal) return -1 * dir;
            if (aVal > bVal) return 1 * dir;
            return 0;
        });

        if (!searchQuery) return sortedItems;
        
        const searchLower = searchQuery.toLowerCase();
        return sortedItems.filter(item => 
            item.name.toLowerCase().includes(searchLower) ||
            item.type.toLowerCase().includes(searchLower) ||
            (item.updatedAt && item.updatedAt.toLowerCase().includes(searchLower)) ||
            (item.createdAt && item.createdAt.toLowerCase().includes(searchLower)) ||
            (item.uuid.toLowerCase().includes(searchLower))
        );
    }, [items, searchQuery, sorting]);

    // --- DATA FETCHING ---
    const fetchAssets = async (pageToFetch = 1) => {
        setLoading(true);
        try {
            const response = await assetService.listAssets({
                type: typeFilter, // Directly use the state from the filter component
                page: pageToFetch,
                limit: pagination.pageSize,
                includePublic: includePublic, // Include public assets if toggled
            });

            // Backend now returns permission data - just ensure name has a default
            const enhancedItems = response.data.map(item => ({
                ...item,
                name: item.name ?? '(Untitled)',
                // Keep permission from backend (already includes role, canRead, canUpdate, canDelete, canShare)
            }));

            setItems(enhancedItems as AssetWithPermission[]);
            setPagination(p => ({
                ...p,
                total: response.pagination.totalItems,
                totalPages: response.pagination.totalPages,
                page: response.pagination.page,
            }));
        } catch (error) {
            toast.error((error as Error).message);
            setItems([]);
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch all available types once on mount for the filter component
    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const types = await assetService.listAssetTypes();
                setAvailableTypes(types);
            } catch (error) {
                toast.error("Failed to load asset types.");
            }
        };
        fetchTypes();
    }, []);

    // Debounced fetch whenever filters change
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchAssets(1);
        }, 300);
        return () => clearTimeout(handler);
    }, [typeFilter, activeOrganization, includePublic]);
    
    const handleUploadComplete = () => {
        setIsUploadPanelOpen(false);
        setIsCreationDialogOpen(false);
        fetchAssets(); // Refresh the list
    };
    
    // --- HANDLERS ---
    const handleSort = (key: string) => {
        setSorting(s => s.sortBy === key 
            ? { ...s, sortDir: s.sortDir === 'asc' ? 'desc' : 'asc' }
            : { sortBy: key, sortDir: 'asc' }
        );
    };
    
    const handleSelectAllOnPage = (checked: boolean | 'indeterminate') => {
        const pageUuids = processedItems.map(i => i.uuid);
        if (checked === true) {
            setSelectedUuids(prev => new Set([...prev, ...pageUuids]));
        } else {
            setSelectedUuids(prev => new Set([...prev].filter(uuid => !pageUuids.includes(uuid))));
        }
    };
    
    const handleCopyLink = (asset: AssetWithPermission) => {
        const publicLink = `${window.location.origin}/assets/${asset.uuid}`;
        navigator.clipboard.writeText(publicLink).then(() => {
            toast.success(`Public link copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    const handleDelete = async (assetToDelete: AssetWithPermission) => {
        if (window.confirm(`Are you sure you want to archive "${assetToDelete.name}"?`)) {
            try {
                await assetService.archiveAsset(assetToDelete.uuid);
                toast.success(`Asset "${assetToDelete.name}" has been archived.`);
                fetchAssets(pagination.page); // Refresh data
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
    };
    
    const openShareModal = (asset: AssetWithPermission) => {
        setShareModalAsset(asset);
        setShareModalOpen(true);
    };

    /**
     * Pre-load sharing info for all owner assets after a delay
     * Cancels pending updates when list data changes (page navigation, filters, etc.)
     */
    useEffect(() => {
        // Clear sharing info when items change (new page, filter, etc.)
        setAssetSharingInfo(new Map());
        setLoadingSharingFor(null);

        // Track if this effect is still active
        let isActive = true;

        // Delay before loading sharing info (500ms - if user navigates away quickly, we skip)
        const loadTimer = setTimeout(() => {
            const ownerAssets = items.filter(item => item.permission?.role === 'owner');

            if (ownerAssets.length === 0) return;

            // Load sharing status for all owner assets in parallel (lightweight endpoint)
            ownerAssets.forEach(async (asset) => {
                try {
                    const status = await assetService.getAssetSharingStatus(asset.uuid);

                    // Only update state if this effect is still active (user hasn't navigated away)
                    if (isActive) {
                        setAssetSharingInfo(prev => new Map(prev).set(asset.uuid, status));
                    }
                } catch (error) {
                    // Only log error if still active
                    if (isActive) {
                        console.error(`Failed to load sharing status for ${asset.uuid}:`, error);
                    }
                }
            });
        }, 500); // 500ms delay - if user navigates away quickly, we cancel

        // Cleanup function - mark this effect as inactive and cancel timer
        return () => {
            isActive = false; // Ignore any pending API responses
            clearTimeout(loadTimer); // Cancel delayed load if user navigates away
        };
    }, [items]); // Re-run when items change (new page, filter, refresh)

    const handleBulkDelete = async () => {
        // Filter out assets that have published services
        const assetsWithServices = Array.from(selectedUuids).filter(uuid => {
            const asset = items.find(a => a.uuid === uuid);
            return asset && asset.data && typeof asset.data === 'object' && 'serviceId' in asset.data;
        });

        const assetsToDelete = Array.from(selectedUuids).filter(uuid => !assetsWithServices.includes(uuid));

        if (assetsWithServices.length > 0) {
            toast.error(`Cannot delete ${assetsWithServices.length} asset(s) with published service(s). Remove the service first.`);
            return;
        }

        if (assetsToDelete.length === 0) {
            toast.error('No assets available to delete');
            return;
        }

        if (window.confirm(`Are you sure you want to archive ${assetsToDelete.length} asset(s)?`)) {
            try {
                await assetService.bulkArchiveAssets(assetsToDelete);
                toast.success(`${assetsToDelete.length} asset(s) have been archived.`);
                setSelectedUuids(new Set()); // Clear selection
                fetchAssets(pagination.page); // Refresh data
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
    };

    // --- RENDERING ---
    const renderDesktopTable = () => (
        <section className="border rounded-md overflow-hidden w-full">
            <ScrollArea className="h-[calc(100vh-320px)] max-h-[600px] w-full">
                <Table className="relative w-full">
                    <TableHeader className="sticky top-0 z-10 bg-background">
                        <TableRow>
                            <TableHead className="w-[50px] p-3 text-center">
                                <SelectAllCheckbox 
                                    items={processedItems} 
                                    selectedUuids={selectedUuids} 
                                    onCheckedChange={handleSelectAllOnPage} 
                                />
                            </TableHead>
                            <TableHead className="p-3 min-w-[150px]">
                                <SortButton column="name" currentSort={sorting} onSort={handleSort} />
                            </TableHead>
                            <TableHead className="p-3 min-w-[100px]">
                                <SortButton column="type" currentSort={sorting} onSort={handleSort} />
                            </TableHead>
                            <TableHead className="p-3 min-w-[120px]">
                                <SortButton column="updatedAt" currentSort={sorting} onSort={handleSort} title="Updated" />
                            </TableHead>
                            <TableHead className="p-3 min-w-[100px]">Status</TableHead>
                            <TableHead className="w-[160px] p-3 text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody className="[&_tr:last-child]:border-0">
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                        ) : processedItems.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                                    No assets found
                                </TableCell>
                            </TableRow>
                        ) : (
                            processedItems.map(asset => {
                                // Helper to check if asset has a published service
                                const hasPublishedService = asset.data && typeof asset.data === 'object' && 'serviceId' in asset.data;
                                const assetServiceId = hasPublishedService ? (asset.data as any).serviceId : null;

                                return (
                                <TableRow
                                    key={asset.uuid}
                                    className={cn("border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted cursor-pointer")}
                                    onClick={() => navigate(`/console/assets/${asset.uuid}`)}
                                    data-state={selectedUuids.has(asset.uuid) ? "selected" : undefined}
                                >
                                    <TableCell className="p-3 w-[50px]" onClick={e => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedUuids.has(asset.uuid)}
                                            onCheckedChange={(checked) => {
                                                setSelectedUuids(prev => {
                                                    const next = new Set(prev);
                                                    if (checked) next.add(asset.uuid);
                                                    else next.delete(asset.uuid);
                                                    return next;
                                                });
                                            }}
                                            aria-label="Select row"
                                        />
                                    </TableCell>
                                    <TableCell className="p-3 font-medium truncate max-w-[200px] md:max-w-[300px] lg:max-w-[400px]" title={asset.name}>
                                        <div className="flex items-center gap-2">
                                            <span>{asset.name}</span>
                                            {/* Service icon - only show if asset has a published service */}
                                            {hasPublishedService && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 px-2 gap-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/console/services/${assetServiceId}`);
                                                    }}
                                                    title="View published service"
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                    <span className="text-xs">Service</span>
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="p-3 text-muted-foreground truncate max-w-[100px] md:max-w-[150px]" title={asset.type}>
                                        {asset.type}
                                    </TableCell>
                                    <TableCell className="p-3 text-muted-foreground min-w-[120px]" title={new Date(asset.updatedAt || asset.createdAt).toLocaleString()}>
                                        {new Date(asset.updatedAt || asset.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="p-3">
                                        <div className="flex items-center gap-2">
                                            <Badge className={getRoleBadgeColor(asset.permission?.role || 'viewer')}>
                                                {getRoleLabel(asset.permission?.role || 'viewer')}
                                            </Badge>

                                            {/* Show sharing indicators for owner assets (auto-loaded after 500ms delay) */}
                                            {asset.permission?.role === 'owner' && assetSharingInfo.has(asset.uuid) && (
                                                <AssetSharingIndicators
                                                    status={assetSharingInfo.get(asset.uuid)!}
                                                />
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="p-3 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1 sm:gap-2">
                                            
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-9 w-9">
                                                        <span className="sr-only">More actions</span>
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {canUserPerformAction(asset.permission?.role, 'read') && (
                                                        <DropdownMenuItem onClick={() => handleCopyLink(asset)}>
                                                            <Copy className="mr-2 h-4 w-4" />
                                                            <span>Copy Asset Link</span>
                                                        </DropdownMenuItem>
                                                    )}
                                                    {canUserPerformAction(asset.permission?.role, 'share') && (
                                                        <DropdownMenuItem onClick={() => openShareModal(asset)}>
                                                            <Users className="mr-2 h-4 w-4" />
                                                            <span>Share</span>
                                                        </DropdownMenuItem>
                                                    )}
                                                    {canUserPerformAction(asset.permission?.role, 'update') && (
                                                        <DropdownMenuItem onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/console/assets/${asset.uuid}`);
                                                        }}>
                                                            <Edit className="mr-2 h-4 w-4" />
                                                            <span>Edit</span>
                                                        </DropdownMenuItem>
                                                    )}
                                                    {canUserPerformAction(asset.permission?.role, 'delete') && (
                                                        <>
                                                            {/* Hide delete option if asset has an active published service */}
                                                            {!hasPublishedService ? (
                                                                <DropdownMenuItem
                                                                    className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:text-red-400 dark:focus:bg-red-900/40"
                                                                    onClick={() => handleDelete(asset)}
                                                                >
                                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                                    <span>Delete</span>
                                                                </DropdownMenuItem>
                                                            ) : (
                                                                <DropdownMenuItem disabled title="Delete published service first">
                                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                                    <span className="text-muted-foreground">Delete (remove service first)</span>
                                                                </DropdownMenuItem>
                                                            )}
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </ScrollArea>
        </section>
    );

    const renderMobileView = () => (
        <ScrollArea className="h-[calc(100vh-280px)] max-h-[500px] w-full">
            <div className="space-y-4 py-1">
                {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className="rounded-lg border p-4">
                            <div className="animate-pulse space-y-3">
                                <div className="flex items-center space-x-4">
                                    <div className="h-12 w-12 rounded-lg bg-muted" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-3/4 rounded bg-muted" />
                                        <div className="h-3 w-1/2 rounded bg-muted" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : processedItems.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
                        No assets found
                    </div>
                ) : (
                    processedItems.map(asset => (
                        <AssetCard
                            key={asset.uuid}
                            asset={asset}
                            onCopyLink={handleCopyLink}
                            onEdit={canUserPerformAction(asset.permission?.role, 'update') ? () => navigate(`/console/assets/${asset.uuid}`) : undefined}
                            onDelete={canUserPerformAction(asset.permission?.role, 'delete') ? handleDelete : undefined}
                        />
                    ))
                )}
            </div>
        </ScrollArea>
    );

    return (
        <ResponsiveContainer>
            <div className="space-y-4 w-full max-w-full">
                <header className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                    <h1 className="text-2xl font-semibold">{title} ({pagination.total})</h1>
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 w-full sm:w-auto">
                        <Button onClick={() => setIsCreationDialogOpen(true)}>
                            <Upload className="mr-2 h-4 w-4" /> Create Asset
                        </Button>
                        {onAdd && <Button onClick={onAdd}>Add Item</Button>}

                        {/* Floating action button for mobile */}
                        {isMobile && (
                            <Button
                                className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg sm:hidden"
                                onClick={() => setIsCreationDialogOpen(true)}
                            >
                                <Upload className="h-6 w-6" />
                            </Button>
                        )}
                    </div>
                </header>

                <AssetFilterControls
                    availableTypes={availableTypes}
                    onFilterChange={(newTypes) => {
                        setTypeFilter(newTypes);
                    }}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    includePublic={includePublic}
                    onIncludePublicChange={setIncludePublic}
                />

                {selectedUuids.size > 0 && (
                    <div className="p-2.5 rounded-md border bg-muted/50 flex flex-col sm:flex-row items-center justify-between gap-2">
                        <span className="text-sm font-medium">{selectedUuids.size} selected</span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setSelectedUuids(new Set())}>Clear</Button>
                            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Desktop table view */}
                <div className="hidden sm:block">
                    {renderDesktopTable()}
                </div>
                
                {/* Mobile card view */}
                <div className="sm:hidden">
                    {renderMobileView()}
                </div>

                <footer className="flex flex-col sm:flex-row items-center justify-between text-sm text-muted-foreground gap-2">
                    <div>{selectedUuids.size} of {pagination.total} selected</div>
                    <div className="flex items-center gap-2">
                        <span>Page {pagination.page} of {pagination.totalPages}</span>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => fetchAssets(pagination.page - 1)} 
                            disabled={pagination.page <= 1}
                        >
                            Previous
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => fetchAssets(pagination.page + 1)} 
                            disabled={pagination.page >= pagination.totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </footer>

                <AssetCreationDialog
                    isOpen={isCreationDialogOpen}
                    onClose={() => setIsCreationDialogOpen(false)}
                    onComplete={handleUploadComplete}
                />
                
                {shareModalOpen && shareModalAsset && (
                    <AssetShareModal
                        asset={shareModalAsset}
                        isOpen={shareModalOpen}
                        onClose={() => setShareModalOpen(false)}
                        onShareComplete={() => {
                            // Optional: refresh asset data or update UI after sharing
                        }}
                    />
                )}

            </div>
        </ResponsiveContainer>
    );
}